/**
 * SalesPageView — SESAAT.AI (UGC Flow concept)
 * Tema terang/bersih, accent indigo, serif italic untuk perkataan tekanan.
 * Layout: Nav → Hero → Logos → Grow → Create(steps) → Creators →
 *         Features → Testimonials → Pricing → CTA → Blogs → Footer.
 * Bahasa: English penuh.
 *
 * NOTA: gambar guna placeholder Unsplash + video Preview guna <video> placeholder.
 *       Tukar src ikut aset sebenar bila ada. Harga = placeholder, sahkan dulu.
 */

import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import SalesNav from './SalesNav';
import PricingSection from './PricingSection';
import useLangganan from '../hooks/useLangganan';
import FaqSection from './FaqSection';

// ── Ikon kecil (inline SVG, elak dependency font ikon) ──────────────────────
const Play = ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
);
const Arrow = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17L17 7M17 7H8M17 7v9" /></svg>
);
const Check = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
);
const Spark = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8L12 2z" /></svg>
);
const PlayCircle = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M10 8l6 4-6 4V8z" fill="currentColor" stroke="none" /></svg>
);
const VolumeOff = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>
);
const VolumeOn = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z" /><path d="M15.54 8.46a5 5 0 0 1 0 7.07" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" /></svg>
);

// ── Data ────────────────────────────────────────────────────────────────────
const STEPS = [
    { n: '1', title: 'Masukkan Data Produk', desc: 'Tambah gambar produk atau tampal pautan TikTok Shop anda.' },
    { n: '2', title: 'Skrip Siap Automatik', desc: 'AI tulis skrip jualan + caption serta-merta, tanpa menaip.' },
    { n: '3', title: 'Jana Gambar & Video di Flow', desc: 'AI hasilkan visual dan video UGC secara automatik.' },
    { n: '4', title: 'Auto-Post ke TikTok', desc: 'Video dimuat naik dan dijadual ke TikTok dengan sendiri.' },
];

const FEATURES = [
    { n: '01', title: 'AI Script Generator', desc: 'Skrip jualan berpsikologi dijana automatik untuk setiap produk.' },
    { n: '02', title: '6 Modul AI', desc: 'AI8SAAT, AI16SAAT, Home Niche, GOYANG, Smart Custom & Flownity.', highlight: true },
    { n: '03', title: 'One-Click Video', desc: 'Jana gambar & video UGC terus melalui automasi Flow.' },
    { n: '04', title: 'Auto-Post TikTok', desc: 'Muat naik & jadual video ke TikTok tanpa sentuh manual.' },
];

const CREATORS = [
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=400&q=80',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=400&q=80',
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80',
    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=400&q=80',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80',
    'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=400&q=80',
];

// Marquee hero. `img` = poster (gambar backup). `video` = fail MP4 sebenar.
//   • Kalau `video` KOSONG → kad papar gambar sahaja (takda apa pecah).
//   • Kalau `video` ADA → marquee main video SENYAP (auto-loop); klik kad → modal main DENGAN audio.
// Letak fail dlm `public/salespage/` → rujuk dgn path cth video: '/salespage/ai8saat.mp4'.
const MODULE_SHOWCASE = [
    { module: 'AI8SAAT',      img: '/salespage/ai8saat.jpg',   video: '', w: 172, off: 34 },
    { module: 'AI16SAAT',     img: CREATORS[1], video: '/salespage/ai16saat.mp4', w: 150, off: 0 },
    { module: 'Home Niche',   img: '/salespage/homeniche.jpg', video: '', w: 194, off: 50 },
    { module: 'GOYANG',       img: '/salespage/goyang.jpg',    video: '', w: 158, off: 12 },
    { module: 'SMART', img: CREATORS[4], video: '/salespage/smartcustom.mp4', w: 170, off: 32 },
    { module: 'FLOWNITY',     img: '/salespage/flownity.jpg',  video: '', w: 148, off: 4 },
];

// NOTA: `proof` = gambar bukti (screenshot jualan/komisen). Placeholder Unsplash buat masa ni —
//       tukar dengan screenshot sebenar bila ada (letak fail dalam src/assets, import, ganti src).
const TESTIMONIALS = [
    { name: 'Sarah Iskandar', role: 'TikTok Shop Affiliate', text: 'It used to take a week to finish 5 videos. Now I push out dozens a day, all auto-posted. Total game changer.', proof: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1000&q=80' },
    { name: 'Faiz Rahman', role: 'Dropship & Marketing', text: 'The AI scripts genuinely sell. No more agonizing over hooks — I just paste the product link and it is done.', proof: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1000&q=80' },
    { name: 'Aina Sofea', role: 'Homemaker / Side Income', text: 'I cannot edit video at all. SESAAT handles everything. Commissions come in even while I am busy with my kids.', proof: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1000&q=80' },
    { name: 'Hafiz Nordin', role: 'Affiliate Marketer', text: 'First month using SESAAT and my commissions doubled. The auto-posting alone saves me hours every single day.', proof: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1000&q=80' },
    { name: 'Nurul Huda', role: 'Small Business Owner', text: 'Honestly did not expect it to be this easy. Paste a link, pick a style, done.' },
    { name: 'Zul Ariffin', role: 'Content Creator', text: 'The hooks it writes are scary good. My watch time went up almost immediately after switching over.' },
    { name: 'Mira Kamal', role: 'Dropshipper', text: 'I run 4 TikTok accounts now and SESAAT feeds all of them. This would have been impossible manually.', proof: 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?auto=format&fit=crop&w=1000&q=80' },
    { name: 'Danial Rosli', role: 'Side Hustler', text: 'From zero video editing skills to posting daily. My first viral video came in week two.' },
    { name: 'Aisyah Latif', role: 'TikTok Affiliate', text: 'What sold me was the speed. I generate a full week of content in one sitting on a Sunday night, then forget about it.' },
    { name: 'Syafiq Aziz', role: 'E-commerce Seller', text: 'Sales up, stress down. That is the whole review.', proof: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1000&q=80' },
    { name: 'Farah Iman', role: 'Marketing Freelancer', text: 'I use it for client accounts now. They think I hired a whole team. It is just me and SESAAT.' },
    { name: 'Amir Hakimi', role: 'Affiliate & Reseller', text: 'Been testing every AI tool out there. This is the first one that actually understands selling, not just writing pretty captions.' },
];

const BLOGS = [
    { img: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&w=600&q=80', title: '7 Hook TikTok Yang Hentikan Scroll Pengguna', date: '12 Jun 2026' },
    { img: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=600&q=80', title: 'Cara Buat Video UGC Menang Tanpa Upah Creator', date: '5 Jun 2026' },
    { img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=600&q=80', title: 'Kenapa Iklan UGC Tukar Lebih Baik Dari Iklan Biasa', date: '28 Mei 2026' },
];

const LOGOS = ['easyTax', 'Eclipsoful', 'Eightbell', 'Elasticware', 'ennLabs', 'Ephemeral'];

// Suis papar/sembunyi section (HIDE, bukan buang). Flip ke `true` bila dah siap edit.
// Section yang false → tak nampak + tak terdeploy sebagai visible, tapi kekal dalam kod.
const SHOW = {
    logos: false,
    grow: false,
    creators: false,
    features: false,
    blogs: false,
};

// ── Halaman ───────────────────────────────────────────────────────────────────
// viewer (optional) = { loggedIn, tier, plan, licenseStatus, allowedModules } dari App.jsx —
// hanya dipass bila render dalam shell logged-in; render public/sesaat.ai kekal anon (tanpa viewer).
const SalesPageView = ({ onNavigateToLogin, onNavigateToPricing, viewer = {} }) => {
    const handleCTA = () => (onNavigateToPricing ? onNavigateToPricing() : onNavigateToLogin?.());
    // ⛔ Butang LANGGAN dalam kotak harga TIDAK boleh guna handleCTA. handleCTA tidak
    //    menerima apa-apa maklumat — ia hanya menukar halaman, jadi tick add-on,
    //    module SMART dan nama pakej DIBUANG dan tiada bil pernah dicipta.
    //    Disahkan hidup 2026-08-29: tekan 'Langgan — RM299' di sesaat.ai cuma pindah
    //    ke /pricing. Pembeli terpaksa tekan TIGA kali sebelum sampai Billplz.
    //    handleCTA KEKAL untuk butang 'Cuba Percuma' — itu memang cuma navigasi.
    const { payLoading, langgan } = useLangganan();
    const goLogin = () => onNavigateToLogin?.();

    // Jumlah pengguna aktif — baca terus dari doc awam public_stats/summary.
    // Di-update oleh Cloud Function (getUserCount + refreshPublicStats sekali/hari).
    // Read pantas + cache, TIADA cold-start callable → angka sebenar konsisten.
    const [userCount, setUserCount] = useState(null);

    // Gambar bukti testimoni yang sedang dibuka dalam modal penuh (null = modal tutup).
    const [proofImg, setProofImg] = useState(null);
    useEffect(() => {
        if (!proofImg) return;
        const onKey = (e) => { if (e.key === 'Escape') setProofImg(null); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [proofImg]);

    // Video marquee dibuka dalam modal (main DENGAN audio sebab dicetus oleh klik user).
    const [videoModal, setVideoModal] = useState(null);

    // Preview video — auto-play + auto-bisu; user tekan butang untuk unmute (dengar audio).
    const previewRef = useRef(null);
    const [previewMuted, setPreviewMuted] = useState(true);
    const togglePreviewMute = () => {
        const v = previewRef.current;
        if (!v) return;
        v.muted = !v.muted;
        if (v.paused) v.play().catch(() => {});
        setPreviewMuted(v.muted);
    };
    useEffect(() => {
        if (!videoModal) return;
        const onKey = (e) => { if (e.key === 'Escape') setVideoModal(null); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [videoModal]);

    useEffect(() => {
        let cancelled = false;
        getDoc(doc(db, 'public_stats', 'summary'))
            .then((snap) => {
                const c = snap.exists() ? snap.data().userCount : null;
                if (!cancelled && typeof c === 'number' && c > 0) setUserCount(c);
            })
            .catch(() => { /* senyap — kekal fallback */ });
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="ugc-page">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Instrument+Serif:ital@0;1&display=swap');

                .ugc-page {
                    --bg: #f4f3ef;
                    --surface: #ffffff;
                    --ink: #14130f;
                    --muted: #6b6a64;
                    --accent: #5b53e8;
                    --accent-soft: #ecebfb;
                    --lime: #bef264;
                    --lime-hover: #d9f99d;
                    --border: #e6e3db;
                    background: var(--bg);
                    color: var(--ink);
                    font-family: 'Inter', sans-serif;
                    overflow-x: clip;
                    -webkit-font-smoothing: antialiased;
                }
                .ugc-page * { box-sizing: border-box; }
                .u-wrap { max-width: 1160px; margin: 0 auto; padding: 0 28px; }
                .serif { font-family: 'Instrument Serif', serif; font-style: italic; font-weight: 400; }
                .u-eyebrow { font-size: 13px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; color: var(--accent); }
                .u-h2 { font-size: clamp(30px, 4.4vw, 46px); font-weight: 700; line-height: 1.08; letter-spacing: -.02em; }
                .u-sub { color: var(--muted); font-size: 17px; line-height: 1.6; max-width: 560px; margin: 14px auto 0; }
                /* Butang utama = LIME (satu-satunya warna aksi) */
                .btn-dark { background: var(--lime); color: var(--ink); border: none; border-radius: 12px; padding: 14px 26px; font: inherit; font-weight: 700; font-size: 15px; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: transform .15s, background .15s; }
                .btn-dark:hover { transform: translateY(-1px); background: var(--lime-hover); }
                .btn-ghost { background: var(--surface); color: var(--ink); border: 1px solid var(--border); border-radius: 10px; padding: 10px 18px; font: inherit; font-weight: 600; font-size: 14px; cursor: pointer; }
                .section { padding: 96px 0; }

                /* NAV */
                .u-nav-baru { position: sticky; top: 0; z-index: 100; background: rgba(244,243,239,.75); backdrop-filter: blur(14px) saturate(1.4); -webkit-backdrop-filter: blur(14px) saturate(1.4); }
                .u-nav { position: sticky; top: 0; z-index: 100; background: rgba(244,243,239,.82); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border); }
                .u-nav-in { display: flex; align-items: center; justify-content: space-between; height: 70px; }
                .u-logo { display: flex; align-items: center; gap: 9px; font-weight: 800; font-size: 19px; letter-spacing: -.01em; }
                .u-logo .mark { width: 26px; height: 26px; border-radius: 8px; background: var(--accent); color: #fff; display: grid; place-items: center; }
                .u-nav-links { display: flex; gap: 34px; font-size: 15px; font-weight: 500; color: var(--muted); }
                .u-nav-links span { cursor: pointer; }
                .u-nav-links span:hover { color: var(--ink); }

                /* HERO */
                .u-hero { text-align: center; padding: 72px 0 40px; position: relative; }
                .u-hero-rays { position: absolute; inset: 0; background:
                    radial-gradient(ellipse 60% 50% at 50% 0%, rgba(91,83,232,.10), transparent 70%);
                    pointer-events: none; }
                .u-trust-pill { display: inline-flex; align-items: center; gap: 8px; background: var(--surface); border: 1px solid var(--border); border-radius: 100px; padding: 6px 14px 6px 8px; font-size: 13px; font-weight: 600; color: var(--muted); margin-bottom: 22px; }
                .u-avatars { display: flex; }
                .u-avatars img { width: 24px; height: 24px; border-radius: 50%; border: 2px solid var(--surface); margin-left: -8px; object-fit: cover; }
                .u-avatars img:first-child { margin-left: 0; }
                .u-h1 { font-size: clamp(40px, 6.4vw, 76px); font-weight: 700; line-height: 1.02; letter-spacing: -.03em; margin: 0 auto; max-width: 900px; }
                .u-hero p { color: var(--muted); font-size: 18px; line-height: 1.6; max-width: 660px; margin: 22px auto 30px; }
                @keyframes u-fyp-fade { 0%, 100% { opacity: 1; } 50% { opacity: .45; } }
                .u-fyp-note { animation: u-fyp-fade 2.4s ease-in-out infinite; }
                @media (prefers-reduced-motion: reduce) { .u-fyp-note { animation: none; } }

                /* HERO video cluster */
                .u-hero-cluster { display: flex; justify-content: center; align-items: flex-end; gap: 18px; margin-top: 56px; position: relative; }
                .u-vcard { border-radius: 18px; overflow: hidden; position: relative; box-shadow: 0 24px 60px rgba(20,19,15,.16); border: 4px solid #fff; }
                .u-vcard img { display: block; width: 100%; height: 100%; object-fit: cover; }
                .u-vcard.sm { width: 168px; height: 240px; }
                .u-vcard.lg { width: 220px; height: 320px; }
                .u-play { position: absolute; inset: 0; margin: auto; width: 52px; height: 52px; border-radius: 50%; background: var(--accent); color: #fff; display: grid; place-items: center; box-shadow: 0 8px 24px rgba(91,83,232,.5); }
                .u-float-badge { position: absolute; background: #fff; border: 1px solid var(--border); border-radius: 100px; padding: 7px 13px; font-size: 12px; font-weight: 700; box-shadow: 0 10px 30px rgba(20,19,15,.12); display: flex; align-items: center; gap: 6px; }

                /* MARQUEE — video contoh module (auto-scroll kiri, loop, gaya influee) */
                .u-marquee { width: 100%; overflow: hidden; margin-top: 52px; padding: 12px 0;
                    -webkit-mask-image: linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent);
                    mask-image: linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent); }
                .u-marquee-track { display: flex; align-items: flex-start; width: max-content; animation: u-marquee 45s linear infinite; }
                .u-marquee:hover .u-marquee-track { animation-play-state: paused; }
                @keyframes u-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
                @media (prefers-reduced-motion: reduce) { .u-marquee-track { animation: none; } }
                .u-mcard { flex: 0 0 auto; margin-right: 16px; background: var(--surface); border: 1px solid var(--border); border-radius: 16px; overflow: hidden; box-shadow: 0 14px 34px rgba(20,19,15,.09); }
                .u-mcard-media { position: relative; aspect-ratio: 9 / 16; }
                .u-mcard-media img, .u-mcard-media video { width: 100%; height: 100%; object-fit: cover; display: block; }
                .u-mcard-media .u-play { width: 42px; height: 42px; }
                .u-mcard-brand { position: absolute; top: 10px; left: 10px; display: flex; align-items: center; gap: 6px; background: rgba(255,255,255,.92); backdrop-filter: blur(4px); border-radius: 100px; padding: 4px 11px 4px 4px; font-size: 12px; font-weight: 700; color: #14130f; }
                .u-mcard-brand .dot { width: 18px; height: 18px; border-radius: 50%; background: linear-gradient(135deg, #8b5cf6, #4f46e5); }
                .u-mcard-cat { position: absolute; bottom: 10px; left: 10px; background: rgba(255,255,255,.92); backdrop-filter: blur(4px); border-radius: 8px; padding: 3px 9px; font-size: 11px; font-weight: 700; color: #14130f; }
                .u-mcard-foot { display: flex; align-items: center; gap: 8px; padding: 10px 12px; }
                .u-mcard-foot .av { width: 24px; height: 24px; border-radius: 50%; object-fit: cover; flex: 0 0 24px; }
                .u-mcard-foot .nm { font-size: 12px; font-weight: 700; line-height: 1.2; }
                .u-mcard-foot .lo { font-size: 11px; color: var(--muted); line-height: 1.2; }

                /* LOGO STRIP */
                .u-logos { padding: 44px 0; border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); }
                .u-logos-track { display: flex; justify-content: center; flex-wrap: wrap; gap: 48px; align-items: center; }
                .u-logos-track span { font-size: 19px; font-weight: 700; color: #b3afa4; letter-spacing: -.01em; }
                .u-logos-cap { text-align: center; font-size: 13px; font-weight: 600; color: var(--muted); margin-bottom: 26px; }

                /* GROW */
                .u-grow-grid { display: grid; grid-template-columns: 1.1fr 1fr 1fr; gap: 20px; margin-top: 52px; }
                .u-tile { border-radius: 20px; overflow: hidden; border: 1px solid var(--border); }
                .u-tile.dark { background: var(--ink); color: #fff; padding: 30px; display: flex; flex-direction: column; justify-content: space-between; min-height: 300px; }
                .u-tile.dark h3 { font-size: 24px; font-weight: 600; line-height: 1.2; letter-spacing: -.01em; }
                .u-tile.img img { width: 100%; height: 100%; object-fit: cover; min-height: 300px; }
                .u-tile.stat { background: var(--surface); padding: 30px; display: flex; flex-direction: column; gap: 22px; justify-content: center; }
                .u-stat .v { font-size: 34px; font-weight: 800; letter-spacing: -.02em; }
                .u-stat .l { font-size: 13px; color: var(--muted); font-weight: 500; }

                /* CREATE (interactive steps card) */
                .u-create-card { background: var(--surface); border: 1px solid var(--border); border-radius: 24px; padding: 28px; display: grid; grid-template-columns: 7fr 3fr; gap: 28px; margin-top: 52px; box-shadow: 0 30px 70px rgba(20,19,15,.06); }
                .u-step { border: 1px solid var(--border); border-radius: 14px; padding: 16px 18px; display: flex; gap: 14px; align-items: flex-start; background: #fcfbf9; }
                .u-step + .u-step { margin-top: 12px; }
                .u-step .num { flex: 0 0 26px; width: 26px; height: 26px; border-radius: 8px; background: var(--accent-soft); color: var(--accent); font-weight: 800; font-size: 13px; display: grid; place-items: center; }
                .u-step h4 { font-size: 15px; font-weight: 700; }
                .u-step p { font-size: 13px; color: var(--muted); margin-top: 2px; line-height: 1.45; }
                .u-gen-btn { width: 100%; justify-content: center; margin-top: 16px; padding: 16px; font-size: 16px; }
                .u-preview-col { display: flex; align-items: stretch; justify-content: center; min-height: 0; }
                .u-preview { position: relative; height: 100%; aspect-ratio: 9 / 16; max-width: 100%; border-radius: 16px; overflow: hidden; border: 1px solid var(--border); background: #000; }
                .u-preview video, .u-preview img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; }
                .u-preview-cap { position: absolute; top: 14px; left: 14px; background: rgba(255,255,255,.9); border-radius: 8px; padding: 4px 10px; font-size: 12px; font-weight: 700; }
                .u-mute-btn { position: absolute; bottom: 14px; right: 14px; width: 44px; height: 44px; border-radius: 50%; border: none; background: rgba(20,19,15,.6); backdrop-filter: blur(6px); color: #fff; display: grid; place-items: center; cursor: pointer; transition: background .15s, transform .15s; z-index: 5; }
                .u-mute-btn:hover { background: rgba(20,19,15,.82); transform: scale(1.06); }

                /* CREATORS */
                .u-creators-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; }
                .u-creators-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 14px; margin-top: 40px; }
                .u-face { aspect-ratio: 3/4; border-radius: 14px; overflow: hidden; border: 1px solid var(--border); }
                .u-face img { width: 100%; height: 100%; object-fit: cover; }

                /* FEATURES */
                .u-feat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-top: 52px; }
                .u-feat { border: 1px solid var(--border); border-radius: 18px; padding: 26px; background: var(--surface); min-height: 230px; display: flex; flex-direction: column; }
                .u-feat.hl { background: linear-gradient(170deg, #6d65f0, #4f47cf); color: #fff; border: none; align-items: center; justify-content: center; text-align: center; }
                .u-feat .fn { font-size: 13px; font-weight: 800; color: var(--muted); }
                .u-feat.hl .fn { color: rgba(255,255,255,.7); }
                .u-feat h3 { font-size: 19px; font-weight: 700; margin-top: auto; letter-spacing: -.01em; }
                .u-feat.hl h3 { font-size: 30px; }
                .u-feat p { font-size: 14px; color: var(--muted); line-height: 1.5; margin-top: 8px; }
                .u-feat.hl p { color: rgba(255,255,255,.85); }

                /* TESTIMONIALS */
                /* Masonry (bertingkat) + fade lut sinar di bawah */
                .u-testi-wrap { position: relative; margin-top: 52px; max-height: 720px; overflow: hidden; }
                .u-testi-grid { column-count: 3; column-gap: 18px; }
                .u-testi-fade { position: absolute; left: 0; right: 0; bottom: 0; height: 240px; pointer-events: none; background: linear-gradient(to bottom, transparent, var(--bg)); }
                .u-testi { break-inside: avoid; -webkit-column-break-inside: avoid; margin: 0 0 18px; width: 100%; border: 1px solid var(--border); border-radius: 18px; background: var(--surface); overflow: hidden; }
                /* Gambar bukti — separuh (crop) di atas kad, klik buka modal penuh */
                .u-testi-proof { display: block; position: relative; width: 100%; height: 150px; padding: 0; border: 0; border-bottom: 1px solid var(--border); background: var(--bg); cursor: pointer; overflow: hidden; }
                .u-testi-proof img { width: 100%; height: 100%; object-fit: cover; object-position: top; display: block; transition: transform .35s ease; }
                .u-testi-proof:hover img { transform: scale(1.04); }
                .u-testi-proof .hint { position: absolute; left: 0; right: 0; bottom: 0; padding: 20px 12px 10px; font-size: 12px; font-weight: 600; color: #fff; text-align: center; background: linear-gradient(transparent, rgba(0,0,0,.55)); }
                .u-testi-body { padding: 24px 26px 26px; }
                .u-testi .stars { color: #f5a623; display: flex; gap: 2px; margin-bottom: 14px; }
                .u-testi p { font-size: 15px; line-height: 1.6; }
                .u-testi .who { display: flex; align-items: center; gap: 11px; margin-top: 20px; }
                .u-testi .who .av { width: 38px; height: 38px; border-radius: 50%; background: var(--accent-soft); color: var(--accent); display: grid; place-items: center; font-weight: 800; }
                .u-testi .who .nm { font-size: 14px; font-weight: 700; }
                .u-testi .who .rl { font-size: 12px; color: var(--muted); }

                /* Modal bukti penuh */
                .u-proof-modal { position: fixed; inset: 0; z-index: 1000; background: rgba(10,10,12,.82); display: flex; align-items: center; justify-content: center; padding: 24px; cursor: zoom-out; animation: u-proof-fade .18s ease; }
                .u-proof-modal img { max-width: min(720px, 92vw); max-height: 88vh; width: auto; height: auto; object-fit: contain; border-radius: 12px; cursor: default; box-shadow: 0 20px 60px rgba(0,0,0,.5); }
                .u-proof-close { position: absolute; top: 18px; right: 22px; width: 42px; height: 42px; border-radius: 50%; border: 0; background: rgba(255,255,255,.14); color: #fff; font-size: 26px; line-height: 1; cursor: pointer; display: grid; place-items: center; transition: background .15s ease; }
                .u-proof-close:hover { background: rgba(255,255,255,.28); }
                @keyframes u-proof-fade { from { opacity: 0; } to { opacity: 1; } }

                /* PRICING */
                .u-price-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; margin-top: 52px; align-items: start; }
                .u-price { border: 1px solid var(--border); border-radius: 20px; padding: 30px; background: var(--surface); position: relative; }
                .u-price.pop { border: 2px solid var(--accent); background: #fbfbff; box-shadow: 0 24px 60px rgba(91,83,232,.14); }
                .u-price .tag { position: absolute; top: -12px; left: 30px; background: var(--accent); color: #fff; font-size: 11px; font-weight: 800; padding: 4px 12px; border-radius: 100px; letter-spacing: .04em; }
                .u-price .nm { font-size: 14px; font-weight: 700; color: var(--muted); }
                .u-price .pr { font-size: 40px; font-weight: 800; letter-spacing: -.02em; margin-top: 6px; }
                .u-price .pr span { font-size: 15px; font-weight: 600; color: var(--muted); }
                .u-price .ds { font-size: 13px; color: var(--muted); margin: 10px 0 20px; line-height: 1.5; }
                .u-price ul { list-style: none; padding: 0; margin: 0 0 24px; display: flex; flex-direction: column; gap: 11px; }
                .u-price li { display: flex; gap: 9px; font-size: 14px; align-items: center; }
                .u-price li .ck { color: var(--accent); display: inline-flex; }
                .u-price .btn-dark, .u-price .btn-ghost { width: 100%; justify-content: center; }

                /* CTA */
                .u-final { text-align: center; }
                .u-final .u-h2 { max-width: 720px; margin: 0 auto; }

                /* BLOGS */
                .u-blog-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; margin-top: 48px; }
                .u-blog { border: 1px solid var(--border); border-radius: 16px; overflow: hidden; background: var(--surface); cursor: pointer; }
                .u-blog img { width: 100%; height: 180px; object-fit: cover; }
                .u-blog .bd { padding: 18px; }
                .u-blog .dt { font-size: 12px; color: var(--muted); }
                .u-blog h4 { font-size: 16px; font-weight: 700; line-height: 1.35; margin-top: 8px; }

                /* FOOTER */
                .u-footer { background: var(--ink); color: #fff; padding: 64px 0 0; margin-top: 40px; }
                .u-foot-top { display: grid; grid-template-columns: 2fr 1fr 1fr; gap: 40px; padding-bottom: 48px; border-bottom: 1px solid rgba(255,255,255,.1); }
                .u-foot-top h5 { font-size: 13px; font-weight: 700; color: rgba(255,255,255,.55); margin-bottom: 16px; letter-spacing: .04em; }
                .u-foot-top a { display: block; color: rgba(255,255,255,.85); font-size: 14px; margin-bottom: 10px; text-decoration: none; cursor: pointer; }
                .u-foot-brand { font-family: 'Instrument Serif', serif; font-style: italic; font-size: clamp(64px, 16vw, 200px); line-height: .9; font-weight: 400; text-align: center; padding: 30px 0; opacity: .96; }

                @media (max-width: 960px) {
                    .u-nav-links { display: none; }
                    .u-grow-grid, .u-create-card, .u-feat-grid, .u-price-grid, .u-blog-grid, .u-foot-top { grid-template-columns: 1fr; }
                    .u-testi-grid { column-count: 2; }
                    .u-creators-grid { grid-template-columns: repeat(3, 1fr); }
                    .u-hero-cluster { flex-wrap: wrap; }
                    .u-foot-brand { font-size: 64px; }
                }

                /* Penalaan telefon */
                @media (max-width: 560px) {
                    .u-wrap { padding: 0 18px; }
                    .section { padding: 60px 0; }
                    /* Hero dimampatkan supaya marquee video naik masuk skrin pertama */
                    .u-hero { padding: 16px 0 8px; }
                    .u-trust-pill { margin-bottom: 18px; font-size: 12px; }
                    .u-h1 { font-size: 34px; letter-spacing: -.02em; line-height: 1.14; }
                    .u-hero p { font-size: 15px; margin: 12px auto 14px; }
                    .u-fyp-note { margin: 0 auto 12px !important; }
                    .u-fyp-note span { font-size: 12px !important; }
                    .u-fyp-note > span { gap: 6px !important; line-height: 1.35 !important; }
                    .u-fyp-note svg { width: 14px !important; height: 14px !important; }
                    .u-hero .btn-ghost, .u-hero .btn-dark { width: 100%; justify-content: center; }
                    .u-marquee { margin-top: 14px; }
                    .u-h2 { font-size: 27px; }
                    .u-sub { font-size: 15px; }
                    .u-testi-grid { column-count: 1; }
                    .u-testi-wrap { max-height: 880px; }
                    .u-create-card { padding: 16px; gap: 18px; }
                    .u-preview { min-height: 300px; }
                    .u-creators-grid { grid-template-columns: repeat(2, 1fr); }
                    .u-creators-head { flex-direction: column; align-items: flex-start; gap: 16px; }
                    .u-mcard { margin-right: 12px; }
                    .u-testi p { font-size: 14px; }
                    .u-price .pr { font-size: 34px; }
                }
            `}</style>

            {/* ── NAV — komponen dikongsi (auto-sync dgn /pricing-baru) ── */}
            <SalesNav onLogin={goLogin} onPricing={onNavigateToPricing} />

            {/* ── HERO ── */}
            <header className="u-hero">
                <div className="u-hero-rays" />
                <div className="u-wrap" style={{ position: 'relative' }}>
                    <div className="u-trust-pill">
                        <div className="u-avatars">
                            {CREATORS.slice(0, 4).map((src, i) => <img key={i} src={src} alt="" />)}
                        </div>
                        {userCount !== null ? `${userCount.toLocaleString()} ejen affiliate guna SESAAT` : '1,000+ ejen affiliate guna SESAAT'}
                    </div>
                    <div className="u-hero-badges" style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#000', color: '#fff', fontSize: 12.5, fontWeight: 600, padding: '4.5px 11.5px', borderRadius: 999, lineHeight: 1 }}><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" /></svg>TikTok</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#EE4D2D', color: '#fff', fontSize: 12.5, fontWeight: 600, padding: '4.5px 11.5px', borderRadius: 999, lineHeight: 1 }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>Shopee</span>
                    </div>
                    <h1 className="u-h1">Buat Video Affiliate guna <span className="serif">6 Teknik Proven Sales!</span></h1>
                    <p>Sehingga 300 video sehari, auto draf ke 3 akaun TikTok anda serentak. Anda cuma pilih produk, pilih module teknik proven sales, tekan run — sistem uruskan dari jana prompt sampai siap draf TikTok!</p>
                    {/* Baris "1 slot beg kuning · kerjasama MCN FYP MEDIA" DIBUANG 2026-08-03 —
                        tawaran MCN tidak lagi ditawarkan (arahan user). Ia janji PENGHANTARAN:
                        selagi ayat ni hidup, setiap pembeli baru layak menuntutnya. Jangan
                        kembalikan tanpa mengesahkan kerjasama MCN memang aktif semula. */}
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 24 }}>
                        <button className="btn-ghost" style={{ padding: '15px 24px', fontSize: 15, display: 'inline-flex', alignItems: 'center', gap: 8 }} onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}>
                            <PlayCircle /> Watch Demo
                        </button>
                        {/* Dulu "Claim 1x TikTok Here" — merujuk claim slot beg kuning MCN yang
                            dah tak ditawarkan. Teks ditukar 2026-08-03 supaya selari dgn CTA lain
                            di page yang sama (Try For Free / Cuba Percuma Sekarang). */}
                        <button className="btn-dark" style={{ padding: '16px 32px', fontSize: 16 }} onClick={handleCTA}>Cuba Percuma <Arrow /></button>
                    </div>

                </div>

                {/* Marquee video contoh setiap module — auto-scroll ke kiri & loop */}
                <div className="u-marquee">
                    <div className="u-marquee-track">
                        {[...MODULE_SHOWCASE, ...MODULE_SHOWCASE, ...MODULE_SHOWCASE, ...MODULE_SHOWCASE].map((m, i) => (
                            <div className="u-mcard" key={i} style={{ width: m.w, marginTop: m.off }}>
                                <div
                                    className="u-mcard-media"
                                    onClick={() => m.video && setVideoModal(m.video)}
                                    style={{ cursor: m.video ? 'pointer' : 'default' }}
                                >
                                    {m.video ? (
                                        <video src={m.video} poster={m.img} autoPlay muted loop playsInline preload="metadata" />
                                    ) : (
                                        <img src={m.img} alt="" />
                                    )}
                                    <div className="u-mcard-brand"><span className="dot" />{m.module}</div>
                                    <div className="u-play" style={{ pointerEvents: 'none' }}><Play size={16} /></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </header>

            {/* ── WATCH DEMO (video embed) ── */}
            <section id="demo" className="section" style={{ paddingTop: 56, paddingBottom: 72, scrollMarginTop: 90 }}>
                <div className="u-wrap" style={{ textAlign: 'center' }}>
                    <span className="u-eyebrow">Tonton Demo</span>
                    <h2 className="u-h2" style={{ marginTop: 12, marginBottom: 28 }}>Lihat SESAAT <span className="serif">beraksi</span></h2>
                    <div style={{ maxWidth: 900, margin: '0 auto', aspectRatio: '16 / 9', borderRadius: 18, overflow: 'hidden', background: '#000', border: '1px solid var(--border)', boxShadow: '0 30px 70px rgba(20,19,15,.12)' }}>
                        <iframe
                            src="https://www.youtube.com/embed/LRv5LOwMRuI?rel=0"
                            title="SESAAT Demo"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                            style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                        />
                    </div>
                </div>
            </section>

            {/* ── LOGO STRIP ── */}
            <section className="u-logos" style={{ display: SHOW.logos ? undefined : 'none' }}>
                <div className="u-wrap">
                    <div className="u-logos-cap">Dipercayai ejen affiliate seluruh Malaysia</div>
                    <div className="u-logos-track">{LOGOS.map((l) => <span key={l}>✷ {l}</span>)}</div>
                </div>
            </section>

            {/* ── GROW ── */}
            <section className="section" style={{ display: SHOW.grow ? undefined : 'none' }}>
                <div className="u-wrap" style={{ textAlign: 'center' }}>
                    <h2 className="u-h2">Kami bantu ejen affiliate jana jualan<br />dengan UGC yang nampak <span className="serif">real, bukan berlakon</span></h2>
                    <p className="u-sub">Video yang manusia rasa percaya — dijana AI dalam saat, bukan minggu.</p>
                    <div className="u-grow-grid">
                        <div className="u-tile dark">
                            <Spark size={26} />
                            <h3>Cipta Iklan UGC Tak Perlu Berminggu-minggu</h3>
                            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.6)' }}>— Pasukan SESAAT</div>
                        </div>
                        <div className="u-tile img"><img src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=600&q=80" alt="" /></div>
                        <div className="u-tile stat">
                            <div className="u-stat"><div className="v">12M+</div><div className="l">Tontonan iklan dijana</div></div>
                            <div className="u-stat"><div className="v">100%</div><div className="l">Kepuasan pelanggan</div></div>
                            <div className="u-stat"><div className="v">+440%</div><div className="l">Peningkatan output konten</div></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CREATE (interactive steps) ── */}
            <section id="features" className="section" style={{ paddingTop: 0, scrollMarginTop: 90 }}>
                <div className="u-wrap" style={{ textAlign: 'center' }}>
                    <h2 className="u-h2">Cipta iklan UGC menjual <span className="serif">dalam beberapa minit</span></h2>
                    <p className="u-sub">Masukkan produk sekali — skrip, video dan posting semuanya jalan sendiri.</p>
                    <div className="u-create-card" style={{ textAlign: 'left' }}>
                        <div>
                            {STEPS.map((s) => (
                                <div className="u-step" key={s.n}>
                                    <div className="num">{s.n}</div>
                                    <div><h4>{s.title}</h4><p>{s.desc}</p></div>
                                </div>
                            ))}
                            <button className="btn-dark u-gen-btn" onClick={handleCTA}>Try For Free <Arrow /></button>
                        </div>
                        <div className="u-preview-col">
                            <div className="u-preview">
                                <div className="u-preview-cap">Preview</div>
                                <video ref={previewRef} src="/salespage/preview.mp4" poster={CREATORS[0]} autoPlay muted loop playsInline preload="metadata" />
                                <button type="button" className="u-mute-btn" onClick={togglePreviewMute} aria-label={previewMuted ? 'Nyahsenyap (dengar audio)' : 'Senyapkan'}>
                                    {previewMuted ? <VolumeOff /> : <VolumeOn />}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CREATORS ── */}
            <section className="section" style={{ display: SHOW.creators ? undefined : 'none' }}>
                <div className="u-wrap">
                    <div className="u-creators-head">
                        <h2 className="u-h2">Pilih ratusan <span className="serif">AI creator</span><br />untuk produk anda</h2>
                        <button className="btn-dark" onClick={handleCTA}>Mula Bina <Arrow /></button>
                    </div>
                    <div className="u-creators-grid">
                        {[...CREATORS, ...CREATORS].slice(0, 6).map((src, i) => (
                            <div className="u-face" key={i}><img src={src} alt="" /></div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FEATURES ── */}
            <section className="section" style={{ display: SHOW.features ? undefined : 'none', background: '#fff', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
                <div className="u-wrap" style={{ textAlign: 'center' }}>
                    <h2 className="u-h2">Semua yang anda perlu untuk<br />cipta iklan UGC yang <span className="serif">menang</span></h2>
                    <p className="u-sub">Satu sistem, dari idea sampai ke posting — tanpa tools berselerak.</p>
                    <div className="u-feat-grid">
                        {FEATURES.map((f) => (
                            <div className={`u-feat${f.highlight ? ' hl' : ''}`} key={f.n}>
                                <div className="fn">{f.n}</div>
                                <h3>{f.title}</h3>
                                <p>{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── TESTIMONIALS ── */}
            <section className="section">
                <div className="u-wrap" style={{ textAlign: 'center' }}>
                    <h2 className="u-h2">Dipercayai ratusan ejen affiliate,<br />terbukti <span className="serif">jana komisen</span></h2>
                    <div className="u-testi-wrap">
                      <div className="u-testi-grid" style={{ textAlign: 'left' }}>
                        {TESTIMONIALS.map((t) => (
                            <div className="u-testi" key={t.name}>
                                {t.proof && (
                                    <button
                                        type="button"
                                        className="u-testi-proof"
                                        onClick={() => setProofImg(t.proof)}
                                        aria-label={`Lihat bukti penuh daripada ${t.name}`}
                                    >
                                        <img src={t.proof} alt={`Bukti hasil ${t.name}`} loading="lazy" />
                                        <span className="hint">🔍 Klik lihat bukti penuh</span>
                                    </button>
                                )}
                                <div className="u-testi-body">
                                    <div className="stars">{[0, 1, 2, 3, 4].map((i) => <span key={i}>★</span>)}</div>
                                    <p>“{t.text}”</p>
                                    <div className="who">
                                        <div className="av">{t.name.charAt(0)}</div>
                                        <div><div className="nm">{t.name}</div><div className="rl">{t.role}</div></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                      </div>
                      <div className="u-testi-fade" aria-hidden="true" />
                    </div>
                </div>
            </section>

            {/* ── PRICING ── */}
            <section className="section" style={{ background: '#fff', borderTop: '1px solid var(--border)' }}>
                <div className="u-wrap" style={{ textAlign: 'center' }}>
                    <h2 className="u-h2">Pakej berbaloi untuk <span className="serif">ejen affiliate</span></h2>
                    <p className="u-sub">Pilih pelan yang sesuai. Naik taraf bila-bila masa.</p>
                    {/* Card pakej + table compare — komponen dikongsi (auto-sync /pricing-baru + StartGate) */}
                    <div style={{ textAlign: 'left' }}>
                        <PricingSection onSubscribe={langgan} payLoading={payLoading} fadeColor="#ffffff" viewer={viewer} />
                    </div>
                </div>
            </section>

            {/* ── FAQ — komponen dikongsi (auto-sync /pricing-baru + StartGate) ── */}
            <section className="section" style={{ background: '#f4f3ef', borderTop: '1px solid var(--border)' }}>
                <FaqSection />
            </section>

            {/* ── FINAL CTA ── */}
            <section className="section u-final">
                <div className="u-wrap">
                    <span className="u-eyebrow">Sedia bermula?</span>
                    <h2 className="u-h2" style={{ marginTop: 14 }}>Sedia cipta iklan UGC yang<br /><span className="serif">betul-betul menjual?</span></h2>
                    <p className="u-sub" style={{ marginBottom: 30 }}>Daripada skrip ke creator ke video siap — semua dalam satu tempat.</p>
                    <button className="btn-dark" style={{ padding: '17px 38px', fontSize: 17 }} onClick={handleCTA}>Cuba Percuma Sekarang <Arrow /></button>
                    <div style={{ marginTop: 16, fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>Tanpa kad kredit. Batal bila-bila masa.</div>
                </div>
            </section>

            {/* ── BLOGS ── */}
            <section className="section" style={{ display: SHOW.blogs ? undefined : 'none', paddingTop: 0 }}>
                <div className="u-wrap">
                    <div className="u-creators-head">
                        <h2 className="u-h2">Blog <span className="serif">terkini</span></h2>
                        <button className="btn-ghost">Lihat Semua</button>
                    </div>
                    <div className="u-blog-grid">
                        {BLOGS.map((b) => (
                            <div className="u-blog" key={b.title}>
                                <img src={b.img} alt="" />
                                <div className="bd"><div className="dt">{b.date}</div><h4>{b.title}</h4></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FOOTER ── */}
            <footer className="u-footer">
                <div className="u-wrap">
                    <div className="u-foot-top">
                        <div>
                            <div className="u-logo" style={{ color: '#fff' }}><span className="mark"><Spark size={15} /></span>SESAAT</div>
                            <p style={{ color: 'rgba(255,255,255,.6)', fontSize: 14, marginTop: 14, maxWidth: 320, lineHeight: 1.6 }}>
                                Automasikan konten TikTok anda dengan kuasa AI — dari idea ke posting, semua auto pilot.
                            </p>
                        </div>
                        <div>
                            <h5>Syarikat</h5>
                            <a>Cara Kerja</a><a>Contoh</a><a onClick={onNavigateToPricing}>Harga</a><a>Blog</a>
                        </div>
                        <div>
                            <h5>Undang-undang</h5>
                            <a>Terma &amp; Syarat</a><a>Privasi</a><a>Iklan</a>
                        </div>
                    </div>
                    <div className="u-foot-brand">SESAAT</div>
                </div>
            </footer>

            {/* ── MODAL BUKTI TESTIMONI ── */}
            {proofImg && (
                <div className="u-proof-modal" onClick={() => setProofImg(null)} role="dialog" aria-modal="true">
                    <button type="button" className="u-proof-close" onClick={() => setProofImg(null)} aria-label="Tutup">×</button>
                    <img src={proofImg} alt="Bukti penuh" onClick={(e) => e.stopPropagation()} />
                </div>
            )}

            {/* ── MODAL VIDEO MARQUEE (main DENGAN audio, dicetus klik) ── */}
            {videoModal && (
                <div className="u-proof-modal" onClick={() => setVideoModal(null)} role="dialog" aria-modal="true">
                    <button type="button" className="u-proof-close" onClick={() => setVideoModal(null)} aria-label="Tutup">×</button>
                    <video
                        src={videoModal}
                        controls
                        autoPlay
                        loop
                        playsInline
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: '92vw', maxHeight: '86vh', borderRadius: 14, background: '#000' }}
                    />
                </div>
            )}
        </div>
    );
};

export default SalesPageView;
